import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

/**
 * One Lighthouse category as a ring with its score inside, shared by the client
 * metrics screen and the monthly report.
 */

// Lighthouse thresholds: 0-49 malo, 50-89 mejorable, 90-100 bueno.
export const scoreColor = (score) => {
  if (score === null || score === undefined) return "#9e9e9e";
  if (score >= 90) return "#0cce6b";
  if (score >= 50) return "#ffa400";
  return "#ff4e42";
};

export function ScoreGauge({ label, score, size = 74, adornment, color: colorOverride }) {
  const theme = useTheme();
  const color = colorOverride || scoreColor(score);
  const hasScore = score !== null && score !== undefined;

  return (
    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        padding: 2,
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={4}
          sx={{ color: alpha(color, 0.18) }}
        />
        <CircularProgress
          variant="determinate"
          value={hasScore ? score : 0}
          size={size}
          thickness={4}
          sx={{ color, position: "absolute", left: 0 }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1.25rem", color }}>
            {hasScore ? score : "—"}
          </Typography>
        </Box>
      </Box>
      <Typography
        variant="body2"
        sx={{ color: theme.palette.text.secondary, textAlign: "center" }}
      >
        {label}
      </Typography>
      {adornment}
    </Box>
  );
}

ScoreGauge.propTypes = {
  label: PropTypes.string.isRequired,
  score: PropTypes.number,
  size: PropTypes.number,
  adornment: PropTypes.node,
  color: PropTypes.string,
};

export default ScoreGauge;
